import type { Meeting } from "../journal/people";
import type { SettlementId } from "../world/types";
import { CELL_TILES, type MapCell, type MapMark, type TrailPoint, type WalkedGround } from "./ground";

/*
 * What the player's map is entitled to show, assembled once per drawing.
 *
 * The rules half of the cartography instrument (`docs/plans/phase-5-plan.md`,
 * decision 10). `map/ground.ts` holds the mask — which cells have been walked
 * and which may be drawn at all — and this decides what may be put *on* those
 * cells, with a stated source for every kind of thing:
 *
 * - **the shading** is the elevation field, sampled only inside the mask
 *   (`world/types.ts`'s `groundLevel` answers everywhere, which is exactly why
 *   the mask rather than the query is the honesty layer);
 * - **the roads** are the simulation's own routes — the pairs a settlement
 *   trades with (`sim_core`'s `history::roads`), which the world lays real
 *   gravel along (`crates/sim-core/src/roadplan.rs`) — clipped cell by cell to
 *   the mask, so a road is on the paper for precisely the stretches the player
 *   has walked beside or seen;
 * - **a settlement is a mark only where the ground under it is known** —
 *   *visited* when the player has stood in its cell, *sighted* when its cell is
 *   inside the surveyed fringe — and a place the player has only been *told
 *   about* gets a name in the margin and no position at all;
 * - **the trail** is the player's own, and its end is where they are;
 * - **the marks** are whatever was written on the paper by hand, which the
 *   store has already refused to accept off known ground (wave 4A's seam).
 *
 * # Why the settlement rule is what it is
 *
 * The obvious source was `WitnessSource.here()`, which is what every other
 * surface in the client uses for "the settlement the player is in". It is the
 * wrong source here: `here()` resolves to the *nearest* town with no bound at
 * all (`world/wasm.ts`), so a player three hundred tiles out in the wild is
 * still "in" somewhere — and a map that marked a town on that authority would
 * be revealing a position for free, which is the one thing decision 10 forbids.
 * The cell test below is the same question asked honestly: did the player's own
 * feet cross the ground the town stands on.
 *
 * And a *name* is not a position. Meeting somebody from Bramblewick teaches the
 * player that Bramblewick exists and what it is called; it teaches them nothing
 * about where it is. So it earns a margin note and never a cross. The client
 * has no other honest source today: a signpost is drawn and cannot be
 * interacted with (`witness/witness.ts`'s `collectSignposts` — it is deliberately
 * not offered to the verb), so there is no observable "read a signpost" event to
 * hang a bearing on. If one ever exists, this is the function it changes.
 *
 * No DOM here. `map/panel.ts` draws what this returns.
 */

/**
 * A road as the map needs it: two ends in world tiles.
 *
 * **A straight line between two named places, and that is the map's own claim
 * rather than the world's.** Since wave 1.5 the road on the ground is terrain
 * that switchbacks up a hillside; this is the *route* it serves, which is what a
 * hand-drawn chart of somewhere you have walked once actually shows. Widening
 * this to the surveyed polyline is a map decision nobody has asked for yet.
 *
 * Structural rather than imported, for the reason `journal/people.ts` gives for
 * the same choice: the player's own instruments do not depend on the rendering
 * layer, they are the thing other modules feed.
 */
export interface MapRoad {
  readonly fromX: number;
  readonly fromY: number;
  readonly toX: number;
  readonly toY: number;
}

/** A settlement as the map needs it. `SettlementView` satisfies it unchanged. */
export interface MapSettlement {
  readonly settlement: SettlementId;
  readonly name: string;
  readonly x: number;
  readonly y: number;
}

/** Everything outside the store that the survey draws on. All of it already on screen. */
export interface SurveyWorld {
  readonly settlements: readonly MapSettlement[];
  readonly roads: readonly MapRoad[];

  /** Who the player has met, for the names in the margin (`journal/people.ts`). */
  readonly met: readonly Meeting[];
}

/**
 * How the player came to know a place well enough to put it on the paper.
 *
 * Two, and the difference is drawn: a town stood in is a town whose name the
 * player has read off its own board and whose streets they have crossed, and a
 * town seen from a hillside is a shape they can point at.
 */
export type PlaceKnowledge = "visited" | "sighted";

/** A settlement the map may show, and how it was earned. */
export interface MapPlace {
  readonly settlement: SettlementId;
  readonly name: string;
  readonly x: number;
  readonly y: number;
  readonly knowledge: PlaceKnowledge;
}

/** One drawn stretch of road, clipped to ground the map knows. */
export interface MapStroke {
  readonly fromX: number;
  readonly fromY: number;
  readonly toX: number;
  readonly toY: number;
}

/** The extent of everything the survey put on the paper, in world tiles. */
export interface MapBounds {
  readonly minX: number;
  readonly minY: number;
  readonly maxX: number;
  readonly maxY: number;
}

/** Everything the panel draws, and nothing it has to work out for itself. */
export interface SurveyedMap {
  readonly cells: readonly MapCell[];

  /** The elevation range across the surveyed cells, for the tint scale. */
  readonly lowest: number;
  readonly highest: number;

  readonly roads: readonly MapStroke[];
  readonly places: readonly MapPlace[];

  /**
   * Places the player has been told the name of and never found: the margin.
   *
   * Names only, in the order they were learned, because a name is all that was
   * learned. See the header.
   */
  readonly heard: readonly string[];

  readonly trail: readonly TrailPoint[];
  readonly marks: readonly MapMark[];

  /** `null` when nothing has been surveyed yet, which is the empty page. */
  readonly bounds: MapBounds | null;

  /** Whether the paper has run out of room (`map/ground.ts`). */
  readonly full: boolean;
}

/**
 * How many cell boundaries one road may be cut at, per axis.
 *
 * A bound rather than a length: a real leg is six hundred tiles and seventy-five
 * cells (`witness/witness.ts`), so this is twenty-five times the honest case and
 * exists only so that a nonsense pair of endpoints costs a partial road instead
 * of a hung tab. A road cut short is the safe failure for this file — the map
 * under-claims, which is the direction it is allowed to be wrong in.
 */
const MAX_CUTS_PER_AXIS = 2048;

/** Cuts shorter than this are dropped rather than drawn, in fractions of a road. */
const CUT_EPSILON = 1e-9;

/**
 * Builds everything the map may show, from the ground the player has covered
 * and the world they have been walking through.
 *
 * Rebuilt whole on each call, like `Journal.byRecord` and `remember`: the caller
 * is a panel somebody opened or a day boundary under one that is already open,
 * and the alternative is an incremental scheme whose bugs look like a map that
 * quietly disagrees with the fog.
 */
export function surveyMap(ground: WalkedGround, world: SurveyWorld): SurveyedMap {
  const cells = ground.cells();
  const bounds = boundsOf(cells);

  let lowest = Number.POSITIVE_INFINITY;
  let highest = Number.NEGATIVE_INFINITY;

  for (const cell of cells) {
    lowest = Math.min(lowest, cell.ground);
    highest = Math.max(highest, cell.ground);
  }

  const places: MapPlace[] = [];
  const positioned = new Set<SettlementId>();

  for (const place of world.settlements) {
    const knowledge: PlaceKnowledge | null = ground.hasWalked(place.x, place.y)
      ? "visited"
      : ground.hasSeen(place.x, place.y)
        ? "sighted"
        : null;

    if (knowledge === null) {
      continue;
    }

    positioned.add(place.settlement);
    places.push({
      settlement: place.settlement,
      name: place.name,
      x: place.x,
      y: place.y,
      knowledge,
    });
  }

  const heard: string[] = [];
  const named = new Set<SettlementId>();

  for (const met of world.met) {
    // The name the *meeting* carried, not one looked up: what the player knows
    // is what they were told, and a settlement the client never saw named
    // leaves this empty (`journal/people.ts`).
    if (met.home === null || met.homeName === "" || positioned.has(met.home) || named.has(met.home)) {
      continue;
    }

    named.add(met.home);
    heard.push(met.homeName);
  }

  const roads: MapStroke[] = [];

  for (const road of world.roads) {
    clipRoad(ground, road, roads);
  }

  return {
    cells,
    lowest: Number.isFinite(lowest) ? lowest : 0,
    highest: Number.isFinite(highest) ? highest : 0,
    roads,
    places,
    heard,
    trail: ground.trail,
    marks: ground.marks,
    bounds,
    full: ground.full,
  };
}

/**
 * Adds the stretches of one road that cross ground the map knows.
 *
 * **Cut at the cell boundaries rather than sampled along the line**, and the
 * difference is the whole reason the honesty claim is checkable: every crossing
 * of every cell edge is a cut, so each interval between two consecutive cuts
 * lies inside exactly one cell, and testing the interval's midpoint decides the
 * whole of it. Sampling every few tiles instead would leave the stretch between
 * two samples answerable by neither, and a road drawn a cell past the fog is
 * the map claiming ground nobody covered.
 */
function clipRoad(ground: WalkedGround, road: MapRoad, into: MapStroke[]): void {
  const spanX = road.toX - road.fromX;
  const spanY = road.toY - road.fromY;

  if (spanX === 0 && spanY === 0) {
    return;
  }

  const cuts = [...axisCuts(road.fromX, spanX), ...axisCuts(road.fromY, spanY), 1];
  cuts.sort((left, right) => left - right);

  let previous = 0;

  for (const cut of cuts) {
    // A sliver: the road grazed a corner, and a stroke of no length is a dot of
    // ink for nothing. The cuts are sorted, so skipping one still advances.
    if (cut - previous <= CUT_EPSILON) {
      previous = cut;

      continue;
    }

    const middle = (previous + cut) / 2;

    if (ground.hasSeen(road.fromX + spanX * middle, road.fromY + spanY * middle)) {
      into.push({
        fromX: road.fromX + spanX * previous,
        fromY: road.fromY + spanY * previous,
        toX: road.fromX + spanX * cut,
        toY: road.fromY + spanY * cut,
      });
    }

    previous = cut;
  }
}

/** Where along a road, in `(0, 1)`, it crosses the cell boundaries of one axis. */
function axisCuts(from: number, span: number): readonly number[] {
  if (span === 0) {
    return [];
  }

  const low = Math.min(from, from + span);
  const high = Math.max(from, from + span);
  const first = Math.ceil(low / CELL_TILES);
  const last = Math.floor(high / CELL_TILES);
  const cuts: number[] = [];

  for (let boundary = first; boundary <= last && cuts.length < MAX_CUTS_PER_AXIS; boundary += 1) {
    const at = (boundary * CELL_TILES - from) / span;

    if (at > 0 && at < 1) {
      cuts.push(at);
    }
  }

  return cuts;
}

/** The extent of the surveyed cells, or `null` for an empty page. */
function boundsOf(cells: readonly MapCell[]): MapBounds | null {
  if (cells.length === 0) {
    return null;
  }

  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;

  for (const cell of cells) {
    minX = Math.min(minX, cell.x);
    minY = Math.min(minY, cell.y);
    maxX = Math.max(maxX, cell.x + CELL_TILES);
    maxY = Math.max(maxY, cell.y + CELL_TILES);
  }

  return { minX, minY, maxX, maxY };
}
